/**
 * The application: shared state providers around the page router.
 *
 * - `AppConfigProvider`: settings read from the API at startup (chain).
 * - `WagmiProvider`: wallet connections for that chain.
 * - `QueryClientProvider`: cache of API data such as the session. Any request the API refuses
 *   because the session has ended ends it on screen too.
 */

import { MAX_CAMPAIGN_DAY_SECONDS, setCampaignDaySeconds } from "@morcast/campaigns";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";
import { WagmiProvider } from "wagmi";

import { ApiRequestError } from "./api/client.js";
import { endEndedSession } from "./auth/session.js";
import { Layout } from "./components/Layout.js";
import { type AppConfig, AppConfigProvider } from "./config.js";
import { Accounts } from "./pages/Accounts.js";
import { AdminHome } from "./pages/admin/AdminHome.js";
import { AdminLayout } from "./pages/admin/AdminLayout.js";
import { AdminAnnouncements } from "./pages/admin/Announcements.js";
import { AdminCampaigns } from "./pages/admin/Campaigns.js";
import { AdminDeployments } from "./pages/admin/Deployments.js";
import { AdminIssues } from "./pages/admin/Issues.js";
import { AdminReviews } from "./pages/admin/Reviews.js";
import { AdminSettlements } from "./pages/admin/Settlements.js";
import { AdminWallets } from "./pages/admin/Wallets.js";
import { BrowseCampaigns } from "./pages/campaigns/Browse.js";
import { CampaignPage } from "./pages/campaigns/Campaign.js";
import { ChangeRequest, NewRequest } from "./pages/campaigns/NewRequest.js";
import { AdminRequestDetail, BrandRequestDetail } from "./pages/campaigns/RequestDetail.js";
import { AdminRequests, MyRequests } from "./pages/campaigns/RequestList.js";
import { Dashboard } from "./pages/Dashboard.js";
import { BusinessModel } from "./pages/docs/BusinessModel.js";
import { DocsLayout } from "./pages/docs/DocsLayout.js";
import { Overview } from "./pages/docs/Overview.js";
import { Protocol } from "./pages/docs/Protocol.js";
import { Results } from "./pages/docs/Results.js";
import { Roadmap } from "./pages/docs/Roadmap.js";
import { Trust } from "./pages/docs/Trust.js";
import { Verification } from "./pages/docs/Verification.js";
import { ForBrands } from "./pages/guides/ForBrands.js";
import { ForCreators } from "./pages/guides/ForCreators.js";
import { Home } from "./pages/Home.js";
import { NotFound, RouteError } from "./pages/NotFound.js";
import { Notifications } from "./pages/Notifications.js";
import { Payouts } from "./pages/Payouts.js";
import { Privacy } from "./pages/Privacy.js";
import { createWagmiConfig } from "./wallet/wagmi.js";

/**
 * Whether to fetch again after a failure: up to three times for network and server errors, never
 * when the API refused the request (4xx), which asking again would not change.
 */
function retryUnlessRefused(failures: number, error: Error): boolean {
  const refused = error instanceof ApiRequestError && error.status >= 400 && error.status < 500;
  return !refused && failures < 3;
}

/** Every page of the app. Pages render inside `Layout`. */
export const routes = [
  {
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      { path: "/", element: <Home /> },
      { path: "/dashboard", element: <Dashboard /> },
      { path: "/brands", element: <ForBrands /> },
      { path: "/creators", element: <ForCreators /> },
      {
        path: "/docs",
        element: <DocsLayout />,
        children: [
          { index: true, element: <Overview /> },
          { path: "business-model", element: <BusinessModel /> },
          { path: "trust", element: <Trust /> },
          { path: "protocol", element: <Protocol /> },
          { path: "verification", element: <Verification /> },
          { path: "results", element: <Results /> },
          { path: "roadmap", element: <Roadmap /> },
        ],
      },
      // The roadmap's first address, kept for links made before it moved into the docs.
      { path: "/roadmap", element: <Navigate to="/docs/roadmap" replace /> },
      { path: "/accounts", element: <Accounts /> },
      { path: "/payouts", element: <Payouts /> },
      { path: "/campaigns", element: <BrowseCampaigns /> },
      { path: "/campaigns/new", element: <NewRequest /> },
      { path: "/campaigns/requests", element: <MyRequests /> },
      { path: "/campaigns/requests/:id", element: <BrandRequestDetail /> },
      { path: "/campaigns/requests/:id/edit", element: <ChangeRequest mode="edit" /> },
      { path: "/campaigns/requests/:id/change", element: <ChangeRequest mode="change" /> },
      // After the static paths above, which take precedence over these parameters.
      { path: "/campaigns/:escrow/:id", element: <CampaignPage /> },
      {
        path: "/admin",
        element: <AdminLayout />,
        children: [
          { index: true, element: <AdminHome /> },
          { path: "campaign-requests", element: <AdminRequests /> },
          { path: "campaign-requests/:id", element: <AdminRequestDetail /> },
          { path: "deployments", element: <AdminDeployments /> },
          { path: "campaigns", element: <AdminCampaigns /> },
          { path: "reviews", element: <AdminReviews /> },
          { path: "issues", element: <AdminIssues /> },
          { path: "settlements", element: <AdminSettlements /> },
          { path: "wallets", element: <AdminWallets /> },
          { path: "announcements", element: <AdminAnnouncements /> },
        ],
      },
      { path: "/notifications", element: <Notifications /> },
      { path: "/privacy", element: <Privacy /> },
      { path: "*", element: <NotFound /> },
    ],
  },
];

export function App({ config }: { config: AppConfig }) {
  // Every campaign deadline the app shows or checks counts in days of the API's length.
  setCampaignDaySeconds(config.campaignDaySeconds ?? MAX_CAMPAIGN_DAY_SECONDS);
  // Created once per app instance, never on re-render.
  const [wagmiConfig] = useState(() => createWagmiConfig(config.chainId));
  const [queryClient] = useState(() => {
    const client: QueryClient = new QueryClient({
      queryCache: new QueryCache({ onError: (error) => endEndedSession(client, error) }),
      mutationCache: new MutationCache({ onError: (error) => endEndedSession(client, error) }),
      defaultOptions: { queries: { retry: retryUnlessRefused } },
    });
    return client;
  });
  const [router] = useState(() => createBrowserRouter(routes));

  return (
    <AppConfigProvider config={config}>
      <WagmiProvider config={wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </WagmiProvider>
    </AppConfigProvider>
  );
}
